import Link from 'next/link';
import { NextSeo } from 'next-seo';

import {
  DocsArticleLayout,
  DocsCallout,
  DocsCodeBlock,
  DocsStep,
  DocsSteps,
} from '@/components/modules/Docs';
import {
  AgentProtocolReference,
  CHATCO_MCP_URL,
  ChatcoDisconnect,
  ConnectorCreditsTable,
  ConnectorErrors,
  ConnectorPrerequisites,
  ConnectorReferences,
  ConnectorStatusPanel,
  ConsentExplainer,
  FirstRunWalkthrough,
  getConnectorHost,
  getConnectorNeighbours,
} from '@/components/modules/Docs/connectors';
import PageLink from '@/utils/PageLink';

const host = getConnectorHost('grok');

const tableOfContents = [
  { id: 'availability', title: '1. Availability' },
  { id: 'before-you-start', title: '2. Before you start' },
  { id: 'connect', title: '3. Add a custom connector' },
  { id: 'approve', title: '4. Approve in Chat.co' },
  { id: 'first-answer', title: '5. Your first cited answer' },
  { id: 'credits', title: '6. Message credits' },
  { id: 'troubleshooting', title: '7. Common errors' },
  { id: 'disconnect', title: '8. Disconnect' },
  { id: 'developer-reference', title: '9. For developers & agents' },
  { id: 'references', title: '10. References' },
];

const stepLink = 'font-medium text-primary-700 underline';

const GrokConnectorGuide = () => (
  <>
    <NextSeo
      title="Use Chat.co experts in Grok"
      description="Add Chat.co to grok.com as a custom connector, sign in with OAuth, and get cited answers from your own Chat.co experts."
      canonical={`https://chat.co${host.href}`}
      openGraph={{
        url: `https://chat.co${host.href}`,
        title: 'Use Chat.co experts in Grok | Chat.co Docs',
        description: 'Add Chat.co to grok.com as a custom connector and get cited answers.',
        type: 'article',
        article: {
          publishedTime: '2026-09-30T00:00:00Z',
          modifiedTime: '2026-09-30T00:00:00Z',
          section: 'Integrations',
          tags: ['grok', 'xai', 'custom connector', 'mcp', 'experts', 'citations'],
        },
      }}
    />
    <DocsArticleLayout
      category="Experts in AI apps"
      categoryColor="text-orange-700"
      title="Use your Chat.co experts in Grok"
      description="Add Chat.co to grok.com as a custom connector and ask your experts for cited answers."
      readingTime="6 min read"
      lastUpdated="September 2026"
      tableOfContents={tableOfContents}
      {...getConnectorNeighbours('grok')}
      keyTakeaways={[
        'On grok.com, Chat.co is added as a custom connector with the server URL.',
        'We tested discovery, a cited answer, a follow-up, a search and opening a source on grok.com.',
        'A Chat.co listing in Grok’s public connector catalogue is not confirmed; use the tested custom setup.',
        'Using the Grok Build terminal agent instead? It has its own guide.',
      ]}>
      <section id="availability" className="scroll-mt-28">
        <h2>1. Availability</h2>
        <ConnectorStatusPanel host={host} />
        <p>
          grok.com lets you bring your own MCP server as a custom connector. We tested Chat.co this
          way: listing experts, a cited answer, a follow-up, a direct search and opening a source.
          This guide covers grok.com; for the terminal agent, see{' '}
          <Link href={PageLink.docsExternalExpertsGrokBuild}>Grok Build</Link>.
        </p>
      </section>

      <section id="before-you-start" className="scroll-mt-28">
        <h2>2. Before you start</h2>
        <ConnectorPrerequisites>
          <li>
            <strong>A Grok account on grok.com</strong> with access to Connectors. In Grok Business
            and Enterprise, xAI notes that a team admin may need to provision connectors first.
            xAI&apos;s documentation does not list plan requirements for custom connectors.
          </li>
        </ConnectorPrerequisites>
      </section>

      <section id="connect" className="scroll-mt-28">
        <h2>3. Add a custom connector</h2>
        <DocsSteps>
          <DocsStep title="Open Connectors">
            <p>
              Go to{' '}
              <a
                href="https://grok.com/connectors"
                target="_blank"
                rel="noopener noreferrer"
                className={stepLink}>
                grok.com/connectors
              </a>
              .
            </p>
          </DocsStep>
          <DocsStep title="Create a custom connector">
            <p>
              Select <strong>New Connector</strong>, then <strong>Custom</strong>.
            </p>
          </DocsStep>
          <DocsStep title="Enter the server URL">
            <DocsCodeBlock code={CHATCO_MCP_URL} language="url" />
            <p>If Grok asks for a name, use Chat.co. Don&apos;t add headers or an API key.</p>
          </DocsStep>
          <DocsStep title="Complete sign-in">
            <p>
              Grok sends you to the Chat.co approval page. Approve it as described in{' '}
              <a href="#approve" className={stepLink}>
                Approve in Chat.co
              </a>
              . Grok then discovers the four tools and makes them available in your conversations.
            </p>
          </DocsStep>
        </DocsSteps>
        <DocsCallout variant="note" title="Catalogue vs custom">
          Grok also has a catalogue of ready-made connectors. We have not confirmed a Chat.co
          listing there, so this guide uses the Custom option, which is the setup we tested.
        </DocsCallout>
      </section>

      <section id="approve" className="scroll-mt-28">
        <h2>4. Approve in Chat.co</h2>
        <ConsentExplainer />
      </section>

      <section id="first-answer" className="scroll-mt-28">
        <h2>5. Your first cited answer</h2>
        <FirstRunWalkthrough where="a new grok.com conversation" />
      </section>

      <section id="credits" className="scroll-mt-28">
        <h2>6. Message credits</h2>
        <ConnectorCreditsTable />
      </section>

      <section id="troubleshooting" className="scroll-mt-28">
        <h2>7. Common errors</h2>
        <ConnectorErrors
          hostRows={[
            [
              'There is no Connectors page, or New Connector is missing',
              'Your Grok account or team may not have connectors enabled. In Business or Enterprise, ask your team admin.',
            ],
            [
              'Grok rejects the URL',
              `Enter exactly ${CHATCO_MCP_URL}, including https:// and /mcp.`,
            ],
            [
              'Grok answers without using Chat.co',
              'Name it in the request: “Use my Chat.co experts to…”.',
            ],
          ]}
        />
      </section>

      <section id="disconnect" className="scroll-mt-28">
        <h2>8. Disconnect</h2>
        <ChatcoDisconnect>
          <p>
            Open{' '}
            <a
              href="https://grok.com/connectors"
              target="_blank"
              rel="noopener noreferrer"
              className={stepLink}>
              grok.com/connectors
            </a>{' '}
            and remove the Chat.co connector.
          </p>
        </ChatcoDisconnect>
      </section>

      <section id="developer-reference" className="scroll-mt-28">
        <h2>9. For developers &amp; agents</h2>
        <AgentProtocolReference />
      </section>

      <section id="references" className="scroll-mt-28">
        <h2>10. References</h2>
        <ConnectorReferences
          vendorLinks={[
            { label: 'xAI: Grok connectors', href: 'https://docs.x.ai/grok/connectors' },
          ]}
        />
      </section>
    </DocsArticleLayout>
  </>
);

export default GrokConnectorGuide;
